import {
  CheckCircleIcon,
  InboxIcon,
  MagnifyingGlassIcon,
  permissionGroups,
  permissionsLabels,
  ShieldCheckIcon,
  XCircleIcon,
  XMarkIcon,
} from "@";
import { useMemo, useState } from "react";
import PermissionGroup from "./PermissionGroup";

const PermissionsGrid = ({
  control,
  watch,
  setValue,
  disabled = false,
  showGlobalSelectAll = false,
}) => {
  const [activeGroupName, setActiveGroupName] = useState(
    permissionGroups[0]?.name,
  );
  const [query, setQuery] = useState("");

  const permissions = watch("permissions") || {};
  const totalKeys = useMemo(() => Object.keys(permissionsLabels), []);
  const enabledCount = totalKeys.filter((k) => permissions[k]).length;
  const totalCount = totalKeys.length;
  const progressPct = Math.round(
    (enabledCount / Math.max(totalCount, 1)) * 100,
  );

  // Map permission key -> group name (for badges in search mode)
  const groupNameByKey = useMemo(() => {
    const map = {};
    permissionGroups.forEach((g) =>
      g.keys.forEach((k) => {
        map[k] = g.name;
      }),
    );
    return map;
  }, []);

  const trimmedQuery = query.trim();
  const isSearching = trimmedQuery.length > 0;
  const searchResults = useMemo(() => {
    if (!isSearching) return [];
    const q = trimmedQuery.toLowerCase();
    return totalKeys.filter((k) => {
      const label = (permissionsLabels[k] || "").toString();
      return label.includes(trimmedQuery) || k.toLowerCase().includes(q);
    });
  }, [isSearching, trimmedQuery, totalKeys]);

  const activeGroup =
    permissionGroups.find((g) => g.name === activeGroupName) ||
    permissionGroups[0];

  const handleSelectAll = () =>
    totalKeys.forEach((k) => setValue(`permissions.${k}`, true));
  const handleClearAll = () =>
    totalKeys.forEach((k) => setValue(`permissions.${k}`, false));

  const getGroupCount = (group) =>
    group.keys.filter((k) => permissions[k]).length;

  const showTopBulkActions = showGlobalSelectAll && !disabled;

  return (
    <div className="space-y-4">
      {/* Summary bar */}
      <div className="card p-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex min-w-0 items-center gap-3">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-purple-800 to-purple-950 text-white ring-1 ring-purple-900/10 dark:from-purple-600 dark:to-purple-800">
              <ShieldCheckIcon className="h-5 w-5" />
            </span>
            <div className="min-w-0">
              <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-purple-700 dark:text-purple-400">
                مركز الصلاحيات
              </p>
              <h3 className="text-base font-bold tracking-tight text-gray-900 dark:text-gray-100">
                <span className="tabular-nums text-purple-900 dark:text-purple-300">
                  {enabledCount}
                </span>
                <span className="mx-1 text-gray-400 dark:text-gray-500">/</span>
                <span className="tabular-nums text-gray-700 dark:text-gray-300">
                  {totalCount}
                </span>
                <span className="ms-1.5 text-xs font-medium text-gray-500 dark:text-gray-400">
                  صلاحية مفعّلة
                </span>
              </h3>
            </div>
          </div>

          <div className="flex flex-1 flex-wrap items-center justify-end gap-2">
            {/* Search */}
            <div className="relative w-full sm:w-72">
              <MagnifyingGlassIcon className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="ابحث عن صلاحية…"
                className="h-9 w-full rounded-full border border-gray-200 bg-white pe-9 ps-9 text-sm text-gray-900 placeholder:text-gray-400 focus:border-purple-400 focus:outline-none focus:ring-2 focus:ring-purple-100 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 dark:placeholder:text-gray-500 dark:focus:border-purple-500 dark:focus:ring-purple-900/40"
              />
              {isSearching && (
                <button
                  type="button"
                  onClick={() => setQuery("")}
                  aria-label="مسح البحث"
                  className="absolute end-2 top-1/2 -translate-y-1/2 rounded-full p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-700 dark:hover:text-gray-200"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              )}
            </div>

            {showTopBulkActions && (
              <>
                <button
                  type="button"
                  onClick={handleSelectAll}
                  disabled={enabledCount === totalCount}
                  className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-white px-3 py-2 text-xs font-semibold text-emerald-700 transition-colors hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-emerald-800/40 dark:bg-gray-800 dark:text-emerald-400 dark:hover:bg-gray-700"
                >
                  <CheckCircleIcon className="h-4 w-4" />
                  تحديد الكل
                </button>
                <button
                  type="button"
                  onClick={handleClearAll}
                  disabled={enabledCount === 0}
                  className="inline-flex items-center gap-1.5 rounded-full border border-rose-200 bg-white px-3 py-2 text-xs font-semibold text-rose-700 transition-colors hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-rose-800/40 dark:bg-gray-800 dark:text-rose-400 dark:hover:bg-gray-700"
                >
                  <XCircleIcon className="h-4 w-4" />
                  مسح الكل
                </button>
              </>
            )}
          </div>
        </div>

        {/* Progress */}
        <div className="mt-4">
          <div className="mb-1.5 flex items-center justify-between text-[11px] font-medium text-gray-500 dark:text-gray-400">
            <span>التقدم</span>
            <span className="font-mono tabular-nums">{progressPct}%</span>
          </div>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-gray-700/60">
            <div
              className="h-full rounded-full bg-gradient-to-l from-purple-600 to-purple-900 transition-all duration-500 ease-out dark:from-purple-500 dark:to-purple-700"
              style={{ width: `${progressPct}%` }}
            />
          </div>
        </div>
      </div>

      {/* Master–Detail */}
      <div className="grid gap-4 lg:grid-cols-12">
        {/* Sidebar: groups */}
        <aside className="card p-4  lg:col-span-4">
          <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3 dark:border-gray-700/60">
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-gray-500 dark:text-gray-400">
              المجموعات
            </p>
            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-bold tabular-nums text-gray-600 dark:bg-gray-700 dark:text-gray-300">
              {permissionGroups.length}
            </span>
          </div>

          <ul
            role="tablist"
            aria-orientation="vertical"
            className="max-h-[560px] overflow-y-auto py-1"
          >
            {permissionGroups.map((group) => {
              const count = getGroupCount(group);
              const total = group.keys.length;
              const isActive =
                !isSearching && group.name === activeGroup.name;
              const allOn = total > 0 && count === total;
              const hasAny = count > 0;

              const chipTone = allOn
                ? "bg-green-500 text-white"
                : hasAny
                  ? "bg-green-100 text-green-800 dark:bg-green-500/50 dark:text-green-300"
                  : "bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400";

              return (
                <li key={group.name}>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    onClick={() => {
                      setActiveGroupName(group.name);
                      setQuery("");
                    }}
                    className={`relative flex w-full rounded-lg mb-1 items-center justify-between gap-3 px-4 py-2 text-start text-sm transition-all ${
                      isActive
                        ? "bg-gradient-to-l from-purple-50 to-purple-50/30 font-semibold text-purple-900 dark:from-purple-900/40 dark:to-purple-900/10 dark:text-purple-200"
                        : "font-medium text-gray-700 hover:bg-purple-50/40 hover:text-purple-900 dark:text-gray-300 dark:hover:bg-gray-700/40 dark:hover:text-purple-200"
                    }`}
                  >
                    {isActive && (
                      <span
                        aria-hidden
                        className="absolute inset-y-2 start-0 w-[3px] rounded-e-full bg-purple-700 dark:bg-purple-400"
                      />
                    )}
                    <span className="truncate">{group.name}</span>
                    <span
                      className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-bold tabular-nums ${chipTone}`}
                    >
                      {count}
                      <span className="opacity-60">/</span>
                      {total}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </aside>

        {/* Content pane */}
        <section className="card lg:col-span-8">
          {isSearching ? (
            searchResults.length > 0 ? (
              <PermissionGroup
                title={`"${trimmedQuery}"`}
                eyebrow={`${searchResults.length} نتيجة بحث`}
                permissionKeys={searchResults}
                control={control}
                watch={watch}
                setValue={setValue}
                disabled={disabled}
                showGroupBadge
                groupNameByKey={groupNameByKey}
              />
            ) : (
              <div className="flex flex-col items-center justify-center px-6 py-20 text-center">
                <span className="mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-gray-100 text-gray-400 dark:bg-gray-700 dark:text-gray-500">
                  <InboxIcon className="h-7 w-7" />
                </span>
                <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                  لا توجد نتائج لـ &quot;{trimmedQuery}&quot;
                </p>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  جرّب كلمة بحث أخرى
                </p>
                <button
                  type="button"
                  onClick={() => setQuery("")}
                  className="mt-4 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  مسح البحث
                </button>
              </div>
            )
          ) : (
            <PermissionGroup
              title={activeGroup.name}
              eyebrow="مجموعة الصلاحيات"
              description={activeGroup.description}
              permissionKeys={activeGroup.keys}
              control={control}
              watch={watch}
              setValue={setValue}
              disabled={disabled}
            />
          )}
        </section>
      </div>
    </div>
  );
};

export default PermissionsGrid;
